<script setup>
    import { computed, inject, toRef, ref } from 'vue';
    import { Field, useField } from 'vee-validate';
    import { string } from 'yup';
    import { defaultFieldProps } from '../../helpers/defaultProps';
    import { useDccsField } from '../../composables/useDccsField';
    import { useI18n } from 'vue-i18n';
    import { useMutationObserver } from '@vueuse/core';

    const { t } = useI18n();

    const input = ref(null);

    const props = defineProps({
        ...defaultFieldProps,

        type: {
            type: String,
            required: true,
            default: 'text',
        },

        pattern: {
            type: String,
            required: false,
            default: null,
        },

        maxlength: {
            type: [Number, String],
            required: false,
            default: null,
        },

        errorMessage: {
            type: String,
            required: false,
            default: null,
        },
    });

    defineEmits(['update:modelValue']);

    const busy = inject('busy');

    const { fieldId, helpId, errorId, normalizedRequired, labelText, setRequired, normalizedMaxlength } = useDccsField(props);
    const validationRules = computed(() => {
        let validator = string();

        if (props.type === 'email') {
            validator = validator.email(t('form.validation.email'));
        }

        if (props.type === 'url') {
            validator = validator.url(t('form.validation.url'));
        }

        if (normalizedRequired.value === true) {
            validator = validator.required(t('form.validation.required', { name: props.label }));
        }

        if (props.pattern !== null) {
            validator = validator.matches(new RegExp(props.pattern));
        }

        return validator;
    });

    const refName = toRef(props, 'name');
    const { errorMessage, value } = useField(refName, validationRules);

    function onClassChange(classAttrValue) {
        const classList = classAttrValue.split(' ');
        if (classList.includes('no-validate')) {
            setRequired(false);
            value.value = '';
        } else if (classList.includes('normal-validate')) {
            setRequired(props.required);
        }
    }

    function getCurrentErrorMessage(propError, validationError, error) {
        let currentErrorMessage;

        if (propError !== null && propError !== undefined) {
            currentErrorMessage = propError;
        } else if (validationError !== null && validationError !== undefined) {
            currentErrorMessage = validationError;
        } else {
            currentErrorMessage = error;
        }

        return currentErrorMessage;
    }

    useMutationObserver(
        input,
        mutations => {
            for (const m of mutations) {
                const newValue = m.target.getAttribute(m.attributeName);
                onClassChange(newValue, m.oldValue);
            }
        },
        { attributes: true, attributeOldValue: true, attributeFilter: ['class'] }
    );
</script>

<template>
    <Field v-slot="{ field, errors }" v-model="value" as="div" :name="name" :label="label" class="dccs-field">
        <label v-if="label && label.length" :class="['dccs-label', { 'has-error': errors.length }]" :for="fieldId">
            {{ labelText }}
        </label>
        <div class="dccs-control">
            <input
                v-bind="field"
                ref="input"
                :name="name"
                :id="fieldId"
                :class="['dccs-input', { 'has-error': errors.length }]"
                :placeholder="placeholder"
                :disabled="busy"
                :maxlength="normalizedMaxlength"
                :autocomplete="autocomplete || undefined"
                :aria-required="normalizedRequired"
                :aria-invalid="errors.length > 0"
                :aria-describedby="
                    [message ? helpId : null, errors.length ? errorId : null].filter(Boolean).join(' ')
                "
            />
        </div>
        <p v-if="message && message.length" :id="helpId" class="dccs-help">
            {{ message }}
        </p>
        <p v-if="errors.length" :id="errorId" class="dccs-help is-danger" role="alert">
            {{ getCurrentErrorMessage(props.errorMessage, errorMessage, errors[0]) }}
        </p>
    </Field>
</template>
